import React, { useState, useEffect } from 'react';
import outskillCert from '../../assets/outskill cert.jpg';
import studiesData from '../../data/achieve-study.json';
import certsData from '../../data/achieve-certs.json';
import { StudyTimeline } from './components/StudyTimeline';
import { CertCard } from './components/CertCard';
import { DocumentModal } from './components/DocumentModal';
import { AchievementFooter } from './components/AchievementFooter';
import type {
  AchievementsProps,
  StudyItem,
  CertItem,
  DocumentModalState,
} from './achievements.types';
import './Achievements.styles.css';

// * Marksheet asset map — keyed by the `marksheet` field in achieve-study.json
const MARKSHEET_MAP: Record<string, string> = {
  outskillCert,
};

const MODAL_CLOSED: DocumentModalState = {
  isOpen: false,
  title: '',
  subtitle: '',
  imageSrc: '',
};

// * [COMPONENT] Studies & Achievements dual-panel section with compact footer
export const Achievements: React.FC<AchievementsProps> = ({
  className = '',
  scale = 1,
}) => {
  const [modal, setModal] = useState<DocumentModalState>(MODAL_CLOSED);
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // * Open marksheet in the shared modal
  const handleViewMarksheet = (study: StudyItem): void => {
    const imageSrc = study.marksheet ? MARKSHEET_MAP[study.marksheet] || '' : '';
    setModal({
      isOpen: true,
      title: study.degree,
      subtitle: `${study.institution} • ${study.startYear}–${study.endYear}`,
      imageSrc,
    });
  };

  // * Open certificate in the shared modal
  const handleSelectCert = (cert: CertItem): void => {
    setModal({
      isOpen: true,
      title: cert.title,
      subtitle: `${cert.issuer} • ${cert.year}`,
      imageSrc: cert.image,
    });
  };

  const handleCloseModal = (): void => {
    setModal(MODAL_CLOSED);
  };

  return (
    <section className={`achievements-section ${className}`}>
      <div
        className="achievements-scalable"
        style={{
          transform: `scale(${scale})`,
          transformOrigin: isMobile ? 'top center' : 'center center',
          transition: 'transform 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        {/* * Split layout: Structured Dual Glass Panels */}
        <div className="achievements-split fp-animate">
          {/* * Left: Education Glass Panel (65%) */}
          <div className="education-glass-panel">
            <StudyTimeline
              studies={studiesData as ReadonlyArray<StudyItem>}
              onViewMarksheet={handleViewMarksheet}
            />
          </div>

          {/* * Right: Achievements Glass Panel (35%) */}
          <div className="achievements-glass-panel">
            <div className="achieve-panel-header">
              <h3 className="achieve-heading">
                <i className="fas fa-trophy" aria-hidden="true" />
                <span>Achievements</span>
              </h3>
            </div>
            <div className="certs-grid">
              {(certsData as CertItem[]).map((cert) => (
                <CertCard
                  key={cert.id}
                  cert={cert}
                  onSelect={handleSelectCert}
                />
              ))}
            </div>
          </div>
        </div>

        {/* * Compact footer positioned cleanly beneath both panels */}
        <AchievementFooter />
      </div>

      {/* * Shared modal for certificates and marksheets */}
      <DocumentModal modal={modal} onClose={handleCloseModal} />
    </section>
  );
};

export default Achievements;
